import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClientForm, toClientPayload, type ClientFormData } from '@/components/clients/ClientForm';

import type { Client } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/clients/new')({ component: ClientNewPage });

function ClientNewPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: ClientFormData) =>
      (await api.post<Client>('/clients', toClientPayload(data, undefined))).data,
    onSuccess: (client) => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client créé avec succès');
      void nav({ to: '/clients/$id', params: { id: client.id } });
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la création')),
  });

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/clients"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">Nouveau client</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>Informations du client</CardTitle></CardHeader>
        <CardContent>
          <ClientForm
            submitLabel="Créer le client"
            pending={mutation.isPending}
            onSubmit={(d) => mutation.mutate(d)}
            onCancel={() => void nav({ to: '/clients' })}
          />
        </CardContent>
      </Card>
    </div>
  );
}
